import {
  getPreviewRecipientKey,
  isRegisteredPreviewSend,
  type DecisionEngineDestinationDefinition,
} from "./definition";
import { getMappingForVariable } from "./sync-config-helpers";

export const eloquaTriggerDefinition: DecisionEngineDestinationDefinition = {
  destinationType: "eloqua",
  category: "trigger",
  generateSyncConfig: ({
    settings,
    campaignId,
    isPreview,
    previewRecipientMode,
  }) => {
    const registeredPreview = isRegisteredPreviewSend({
      isPreview,
      previewRecipientMode,
    });
    const previewRecipientKey = getPreviewRecipientKey(settings);
    return {
      type: "trigger",
      emailId: campaignId,
      // Registered mode without a key falls back to email keying even on id
      // channels: the registered recipient is the account address, so
      // external_id would otherwise be sent as a nonexistent contact ID.
      contactMapping:
        registeredPreview && previewRecipientKey
          ? {
              type: "static",
              to: "id",
              value: previewRecipientKey,
              valueType: "STRING",
            }
          : registeredPreview
            ? { to: "C_EmailAddress", from: "external_id" }
            : {
                to:
                  settings.recipientType === "email" ? "C_EmailAddress" : "id",
                from: "external_id",
              },
      ...(settings.sendFrom ? { sendFrom: settings.sendFrom } : {}),
      sendOptions: { allowResend: true },
    };
  },
  syncStrategy: {
    supportsChannelLevelSync: false,
  },
};

export const eloquaCdoDefinition: DecisionEngineDestinationDefinition = {
  destinationType: "eloqua",
  category: "event",
  producers: ["aid"],
  generateSyncConfig: ({
    settings,
    campaignId,
    resourceId,
    isPreview,
    sendToHightouchUser,
    previewRecipientMode,
    messageVariables = [],
  }) => {
    const registeredPreview = isRegisteredPreviewSend({
      isPreview,
      previewRecipientMode,
    });
    const previewRecipientKey = getPreviewRecipientKey(settings);
    return {
      mode: "insert",
      type: "object",
      object: "customObjects",
      customObjectId: campaignId,
      mappings: [
        // In a registered preview the row id IS the AidPreviewTraceId, so the
        // interactionId field doubles as the trace carrier. The published
        // snippet is a field merge over it; no extra CDO field needed.
        { type: "standard", from: "id", to: "interactionId1" },
        { type: "standard", from: "send_at", to: "sendAt1" },
        registeredPreview && previewRecipientKey
          ? {
              type: "static",
              to: "contactField1",
              value: previewRecipientKey,
              valueType: "STRING",
            }
          : { type: "standard", from: "external_id", to: "contactField1" },
        ...(isPreview || sendToHightouchUser
          ? [
              {
                type: "static",
                to: "isTest1",
                value: "true",
                valueType: "BOOLEAN",
              },
            ]
          : []),
        ...(resourceId
          ? [
              {
                type: "static",
                to: "templateId1",
                value: resourceId,
                valueType: "STRING",
              },
            ]
          : []),
        ...messageVariables.map((variable) => ({
          ...getMappingForVariable(variable.name, `${variable.name}1`),
        })),
      ],

      customObjectMapping: {
        to: registeredPreview
          ? previewRecipientKey
            ? "Id"
            : "C_EmailAddress"
          : settings.recipientType === "email"
            ? "C_EmailAddress"
            : "Id",
        from: "contactField1",
        type: "destinationOnlyMapping",
      },
      hasCustomObjectMapping: "contacts",
      mapDataCardsCaseSensitiveMatch: "true",
    };
  },
  // 500k is ABSOLUTE_OUTER_BATCH_SIZE_CAP in the Eloqua destination form; the
  // parallelization form field has no cap, so 100 just keeps overrides sane.
  // No mode knob — the `mode: "insert"` emitted above is load-bearing.
  syncTuning: {
    batchSize: { configKey: "rowsPerBatch", max: 500_000 },
    parallelization: { max: 100 },
  },
  syncStrategy: {
    supportsChannelLevelSync: false,
  },
  resourceLifecycle: {
    requiresResourceUpdateOnVariableChange: true,
  },
};
